import { motion } from 'framer-motion';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import dotsVerticalIcon from '../../images/dots-vertical.svg';
import { Colors } from '../../themes/colors';

const opacityVariants = {
  show: { opacity: 1, display: 'flex' },
  hide: { opacity: 0, transitionEnd: { display: 'none' } },
};

interface IEventBus {
  callbacks: Array<() => void>;
  subscribe: (callback: () => void) => void;
  unsubscribe: (callback: () => void) => void;
  notify: () => void;
}

// Event Bus for managing communication between DotsMenu instances
const eventBus: IEventBus = {
  callbacks: [],
  // Subscribe to the event bus
  subscribe(callback: () => void) {
    this.callbacks.push(callback);
  },
  // Unsubscribe from the event bus
  unsubscribe(callback: () => void) {
    this.callbacks = this.callbacks.filter((cb) => cb !== callback);
  },
  // Notify all subscribers
  notify() {
    this.callbacks.forEach((callback) => callback());
  },
};

interface IDotsMenuProps {
  children: ReactNode;
  CustomMenuIcon?: ComponentType<{ isHovered: boolean; isDisabled: boolean }>;
  listener?: () => void;
  disabled?: boolean;
  position?: 'left' | 'bottom' | 'right' | 'bottom-right' | 'top';
  iconSrc?: string;
  onValidate?: () => boolean;
  dataCy?: string;
}

export function DotsMenu({
  children,
  CustomMenuIcon,
  listener,
  disabled = false,
  position = 'left',
  iconSrc = dotsVerticalIcon,
  onValidate = () => true,
  dataCy,
}: IDotsMenuProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculateMenuPosition = useCallback(() => {
    if (!menuRef.current) return;

    const trigger = menuRef.current.getBoundingClientRect();
    const menuOffset = 2; // Distance from trigger element
    let top = 0;
    let left = 0;

    if (position === 'bottom-right') {
      top = trigger.bottom + menuOffset;
      left = trigger.left - trigger.width * 0.3;
    } else if (position === 'bottom') {
      top = trigger.bottom + menuOffset * 2;
      left = trigger.left - trigger.width * 0.3;
    } else if (position === 'right') {
      top = trigger.top - trigger.height * 0.4;
      left = trigger.right + menuOffset;
    } else if (position === 'top') {
      top = trigger.top - menuOffset;
      left = trigger.left - trigger.width * 0.1;
    } else {
      // Default position is 'left' - dropdown appears to the left of the button
      top = trigger.top - trigger.height * 0.4;
      left = trigger.left - menuOffset;
    }

    setMenuCoords({ top, left });
  }, [position]);

  // Toggle the tooltip visibility
  const toggleTooltip = () => {
    if (showTooltip) {
      setShowTooltip(false);
    } else {
      eventBus.notify(); // Notify all other DotsMenu instances to close
      listener?.();
      calculateMenuPosition(); // Calculate position before showing
      setShowTooltip(true);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const isClickInsideMenu = menuRef.current?.contains(event.target as Node);
    const isClickInsideDropdown = dropdownRef.current?.contains(event.target as Node);

    if (!isClickInsideMenu && !isClickInsideDropdown) {
      setShowTooltip(false);
    }
  };

  useEffect(() => {
    const handleClose = () => setShowTooltip(false);
    document.addEventListener('click', handleClickOutside);
    eventBus.subscribe(handleClose);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      eventBus.unsubscribe(handleClose);
    };
  }, []);

  // Update menu position on scroll and resize
  useEffect(() => {
    if (!showTooltip) return;

    const updatePosition = () => {
      calculateMenuPosition();
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showTooltip, calculateMenuPosition]);

  // Get transform based on position for proper alignment
  const getTransform = (): string => {
    if (position === 'bottom-right' || position === 'bottom') {
      return 'translate(0, 0)';
    }
    if (position === 'top') {
      return 'translate(0, -100%)';
    }
    if (position === 'left') {
      return 'translate(-100%, 0)';
    }
    // right
    return 'translate(0, 0)';
  };

  // Render dropdown via Portal
  const dropdownPortal = showTooltip
    ? createPortal(
      <motion.div
          ref={dropdownRef}
          initial="hide"
          transition={{ duration: 0.1 }}
          animate="show"
          variants={opacityVariants}
          style={{
            position: 'fixed',
            top: `${menuCoords.top}px`,
            left: `${menuCoords.left}px`,
            transform: getTransform(),
            width: 'max-content',
            backgroundColor: Colors.white,
            padding: '5px',
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
            zIndex: 4,
          }}
        >
        <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
          {children}
        </div>
      </motion.div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        data-cy={dataCy}
        ref={menuRef}
        type="button"
        style={{
          all: 'unset',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        disabled={disabled}
        onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          if (onValidate?.()) {
            toggleTooltip();
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.menu}>
          {CustomMenuIcon ? (
            <CustomMenuIcon isHovered={isHovered} isDisabled={disabled} />
          ) : (
            <div style={styles.icon}>
              <img
                src={iconSrc}
                alt="menu_icon"
                style={{
                  width: 24,
                  height: 24,
                }}
              />
            </div>
          )}
        </div>
      </button>
      {dropdownPortal}
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  menu: {
    position: 'relative',
    display: 'inline-block',
  },
  icon: {
    cursor: 'pointer',
    display: 'flex',
    width: 32,
    height: 32,
  },
  tooltip: {
    width: 'max-content',
    position: 'absolute',
    backgroundColor: Colors.white,
    padding: '5px',
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
    zIndex: 4,
  },
};
